import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import {marked} from 'marked';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {MatDialog} from '@angular/material/dialog';
import {ResearchPaperDTO} from '../../../research/dto/research-paper';
import {
  ResearchPaperDialogComponent
} from '../../../research/components/research-paper-dialog/research-paper-dialog.component';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {PaperOverlayComponent} from '../paper-overlay/paper-overlay.component';


@Component({
  selector: 'app-sourced-markdown',
  imports: [],
  templateUrl: './markdown.component.html',
  styleUrl: './markdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomMarkdownComponent implements OnInit, OnChanges, OnDestroy {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly overlay: Overlay = inject(Overlay);

  readonly markdown = input.required<string>();
  readonly papers = input.required<ResearchPaperDTO[]>();
  readonly linkCountStartNumber = input<number>(1);
  readonly isSmallScreen = input<boolean>(false);
  private idMap: Record<string, number> = {};
  private linkCounter = 1;

  parsedMarkdown: SafeHtml = '';
  private tooltipOverlayRef: OverlayRef | null = null;

  ngOnInit(): void {
    this.linkCounter = this.linkCountStartNumber();
    marked.use({
      hooks: {
        postprocess: (markdown: string) => this.postProcessMarkdown(markdown)
      }
    });
    if (this.markdown().length > 0) {
      this.parsedMarkdown = this.sanitizer.bypassSecurityTrustHtml(marked.parse(this.markdown()) as string);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markdown']) {
      this.parsedMarkdown = this.sanitizer.bypassSecurityTrustHtml(marked.parse(this.markdown()) as string);
      this.onLinkHoverLeave(undefined);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    if (this.tooltipOverlayRef) {
      this.tooltipOverlayRef.dispose();
    }
  }

  private postProcessMarkdown(markdown: string): string {
    return markdown.replace(/\[[^\]]{0,40}\]/g, (match) => {
      const clearMatch = match.replace(/[\[\]]/g, '');
      const matches = clearMatch.split(',');
      const links = matches.map(match => match.trim())
        .map(match => {
          const paper = this.getPaper(+clearMatch);
          if (!paper) {
            return match;
          }
          if (!this.idMap[clearMatch]) {
            this.idMap[clearMatch] = this.linkCounter++;
          }
          const displayNumber = this.idMap[clearMatch];
          return `<a href="#" data-ref="${clearMatch}"  matTooltip="Paper: ${paper.title}" class="ref-link">${displayNumber}</a>`;
        });
      return `[${links.join('')}]`;
    });
  }

  onLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('ref-link')) {
      event.preventDefault();
      const refId = target.getAttribute('data-ref');
      if (refId) {
        const source = this.getPaper(+refId);
        if (source) {
          this.dialog.open(ResearchPaperDialogComponent, {
            data: {
              isMobile: this.isSmallScreen(),
              paper: source
            },
            autoFocus: false,
            width: this.isSmallScreen() ? '100vw' : '90%',
            height: this.isSmallScreen() ? '100vh' : '90%',
            maxWidth: '100vw',
          });
        }
      }
    }
  }

  onLinkHover(event: MouseEvent): void {
    const refLink = (event.target as HTMLElement).closest('.ref-link');
    if (refLink) {
      const refId = refLink.getAttribute('data-ref');
      if (refId) {
        const paper = this.getPaper(+refId);
        if (paper) {
          this.tooltipOverlayRef = this.overlay.create({
            positionStrategy: this.overlay.position()
              .flexibleConnectedTo(refLink)
              .withPositions([{
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top'
              }]),
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: false,
          });
          const tooltipPortal = new ComponentPortal(PaperOverlayComponent, this.viewContainerRef);
          const tooltipRef = this.tooltipOverlayRef.attach(tooltipPortal);
          tooltipRef.instance.paper = signal(paper);
        }
      }
    }
  }

  onLinkHoverLeave(event?: MouseEvent): void {
    if (this.tooltipOverlayRef) {
      this.tooltipOverlayRef.dispose();
      this.tooltipOverlayRef = null;
    }
  }


  getPaper(paperId: number): ResearchPaperDTO | undefined {
    if (!paperId) {
      return undefined;
    }
    return this.papers().find(source => source.id === paperId);
  }

}
